import { useCallback, useEffect, useMemo, useState } from "react";

import { Box, Flex, Image } from "@chakra-ui/react";
import { datadogRum } from "@datadog/browser-rum";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { TGTGBagAdditionMethod } from "analytics/events";
import { useAppLanguageStore } from "app/stores/useAppLanguageStore";
import { routes } from "config/routes";
import { EndDroppingProcessButton } from "flows/Inbound/components/EndDroppingProcessButton";
import { SpinnerModal } from "shared/components/SpinnerModal";
import { serializeSearchUnitWithPriceResult } from "shared/models/products/serializer";
import { TranslatedProductWithPrice } from "shared/models/products/types";
import { useSearchUnitsByTextWithPriceLazyQuery } from "shared/queries/products/products.generated";
import { useProductSearchStore } from "shared/stores/useProductSearchStore";
import { Status } from "ui/Status/Status";
import { BodyS, DetailM, TitleM, TitleXS } from "ui/Typography/Typography";
import { countryNameFromCode } from "utils/countryNames";

import { useAddProductToBag } from "../hooks/useBagProductActions";
import { useTGTGBagStore } from "../stores/useTGTGBagStore";
import { isProductEligibleForBag } from "../utils/helpers";

interface SelectedStates {
  [key: string]: boolean;
}

function NoResultsFound() {
  return (
    <Flex direction="column" flex={1} justifyContent="center" textAlign="center" px="s100">
      <TitleM>
        <FormattedMessage id="generic.warnings.no-results-found" />
      </TitleM>
      <BodyS>
        <FormattedMessage id="generic.info.no-results-found" />
      </BodyS>
    </Flex>
  );
}

function ProductCard({
  result,
  selectedStates,
  toggleSelection,
  isEligible,
  notEligibleLabel,
}: {
  result: TranslatedProductWithPrice;
  selectedStates: SelectedStates;
  toggleSelection: (id: string) => void;
  isEligible: boolean;
  notEligibleLabel: string;
}) {
  const { productImageUrl, productName, countryOfOriginName, productSku, id } = result;
  return (
    <Flex
      onClick={isEligible ? () => toggleSelection(id) : undefined}
      alignItems="center"
      p="s200"
      pos="relative"
      bg="white"
      cursor={isEligible ? "pointer" : "default"}
    >
      <Box
        pos="absolute"
        bg="grey.800"
        top="0"
        left="0"
        height="100%"
        width="100%"
        transition="opacity 50ms linear"
        opacity={selectedStates[id] ? 0.1 : 0}
      />
      <Flex
        bg="grey.100"
        borderRadius="8px"
        h="80px"
        w="80px"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        {productImageUrl && <Image maxHeight="100%" objectFit="contain" src={productImageUrl} />}
      </Flex>
      <Flex gap="s200" alignItems="center" justifyContent="center">
        <Flex direction="column" ml="s200">
          <TitleXS color="grey.800" noOfLines={3}>
            {`${productName} ${countryOfOriginName ? ` (${countryOfOriginName})` : ""}`}
          </TitleXS>
          <DetailM color="grey.600">{` SKU ${productSku}`}</DetailM>
        </Flex>
        {!isEligible && (
          <Flex>
            <Status
              size="small"
              label={notEligibleLabel}
              bg="red.100"
              textColor="red.500"
              whiteSpace="nowrap"
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

export function TextSearchProductList() {
  const appLanguage = useAppLanguageStore((state) => state.appLanguage);
  const navigate = useNavigate();
  const intl = useIntl();
  const { allowedCategories, excludedCategories } = useTGTGBagStore(
    useShallow((state) => ({
      allowedCategories: state.allowedCategories,
      excludedCategories: state.excludedCategories,
    })),
  );

  const { textSearchQuery, resetTextSearch, setIsInvalid } = useProductSearchStore(
    useShallow((state) => ({
      textSearchQuery: state.textSearch,
      resetTextSearch: state.resetTextSearch,
      setIsInvalid: state.setIsInvalid,
    })),
  );

  const [searchUnitsByText, { data, loading }] = useSearchUnitsByTextWithPriceLazyQuery({
    fetchPolicy: "no-cache",
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      datadogRum.addError("Error While Searching units by text", { errorMessage });
    },
  });

  useEffect(() => {
    const debounceSearch = setTimeout(() => {
      if (!textSearchQuery) return;

      const query = textSearchQuery.trim();
      if (query.length < 3) return;

      const isAllDigits = /^\d+$/.test(query);
      if (isAllDigits && query.length >= 11) {
        setIsInvalid(true);
        return;
      }

      setIsInvalid(false);
      searchUnitsByText({
        variables: { searchUnitsByTextInput: { query } },
      });
    }, 300);
    return () => clearTimeout(debounceSearch);
  }, [textSearchQuery, searchUnitsByText, setIsInvalid]);

  const searchResults = useMemo(() => {
    return data
      ? data.searchUnitsByText.units
          .filter((unit) => unit.type === "single")
          .map(serializeSearchUnitWithPriceResult)
      : null;
  }, [data]);

  const productResults = useMemo(() => {
    if (!searchResults) return [];

    const translatedResults = searchResults.map((unit) => ({
      ...unit,
      countryOfOriginName: countryNameFromCode(appLanguage, unit.countryOfOriginCode) ?? "",
    }));
    return Array.from(
      new Map(translatedResults.map((product) => [product.productSku, product])).values(),
    );
  }, [appLanguage, searchResults]);

  const noResults = useMemo(
    () => textSearchQuery.length > 2 && productResults.length === 0,
    [textSearchQuery.length, productResults],
  );

  const [selectedStates, setSelectedStates] = useState<SelectedStates>({});

  const selectedResults = useMemo(
    () => productResults.filter(({ id }) => selectedStates[id]),
    [selectedStates, productResults],
  );

  const productWithoutPrice = useMemo(
    () => selectedResults.find((product) => product.productPrice === null),
    [selectedResults],
  );

  const toggleSelection = useCallback((id: string) => {
    setSelectedStates((prevState) => ({ ...prevState, [id]: !prevState[id] }));
  }, []);

  const { setProductToAdd, setShowProductNotListedModal } = useTGTGBagStore(
    useShallow((state) => ({
      setProductToAdd: state.setProductToAdd,
      setShowProductNotListedModal: state.setShowProductNotListedModal,
    })),
  );
  const addProductToBag = useAddProductToBag();
  const handleAddToList = useCallback(() => {
    if (productWithoutPrice) {
      setProductToAdd(productWithoutPrice);
      setShowProductNotListedModal(true);
    } else {
      selectedResults.forEach((result) => {
        addProductToBag(result, TGTGBagAdditionMethod.TextSearched);
      });
    }
    resetTextSearch();
    navigate(routes.inventory.tooGoodToGo.prepareBag);
  }, [
    addProductToBag,
    navigate,
    productWithoutPrice,
    resetTextSearch,
    selectedResults,
    setProductToAdd,
    setShowProductNotListedModal,
  ]);

  if (loading) return <SpinnerModal isOpen />;
  if (!productResults) return null;

  return (
    <Flex
      direction="column"
      w="100%"
      gap="s50"
      bg="grey.150"
      h={noResults ? "100%" : undefined}
      overflow="scroll"
      css={{
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      {noResults ? (
        <NoResultsFound />
      ) : (
        <>
          {productResults.map((result) => (
            <ProductCard
              key={result.id}
              result={result}
              selectedStates={selectedStates}
              toggleSelection={toggleSelection}
              isEligible={isProductEligibleForBag(
                result.productClassification,
                allowedCategories,
                excludedCategories,
              )}
              notEligibleLabel={intl.formatMessage({
                id: "pages.inventory.too-good-to-go.not-eligible",
              })}
            />
          ))}
          <Box mb="60px" />
          <EndDroppingProcessButton
            shouldShowButton={selectedResults.length > 0}
            onClickButton={handleAddToList}
            labelMessageId="generic.actions.add-to-list"
            bottom="0"
            p="s200"
          />
        </>
      )}
    </Flex>
  );
}
